/**
 * eventTextMeReminder.ts — "💬 Text me about this one", her item 11 of the
 * 2026-10-31 reminders batch. #1966.
 *
 * ⚠️ "Item 11" is Kasandra's own fast-path numbering, not a GitHub number.
 * #1966 is the record issue; `#11` would point at unrelated work.
 *
 * ── WHAT THIS IS, AND WHY IT IS NOT buildTimedReminderRow ──────────────────
 *
 * The row this writes IS a `timed_reminder` — no new task_type, because a new
 * one needs three gates (the RLS policy, the task_type CHECK and the runner's
 * switch) and silently 23514s or no-ops if you miss one. So the runner, the
 * SMS text, the push and the Tasks card all already work.
 *
 * What is different is HOW the fire time is decided, and it is the reason this
 * is a separate builder rather than a call into
 * `supabase/functions/kin-chat/scheduledTaskBuilder.ts`:
 *
 *   - That builder takes a time SHE NAMED. This one derives it from an event,
 *     so the input is an event plus an offset and the clock time is an output.
 *   - ⚠️ That builder REFUSES a time inside quiet hours, which is right when
 *     she is typing a time and Kin can answer. Here there is nothing to
 *     negotiate: she picked "1 day before" on a 6am soccer game, and refusing
 *     the whole reminder over an offset she did not choose in a window she
 *     cannot see would be absurd. Kasandra's call was to SNAP and say so.
 *   - It lives in a Deno module tree and reads a different column set.
 *
 * ── ⚠️ THE CLAMP MUST MATCH `sync_event_reminder()` EXACTLY ────────────────
 *
 * The trigger in `20261031150000_scheduled_tasks_event_id.sql` recomputes this
 * row's slot on every event UPDATE. If this file and that function disagree,
 * her reminder MOVES the first time she edits the event — and the direction of
 * the disagreement decides whether it moves earlier (a text at 6am) or
 * vanishes (retired as unplaceable). So every rule below cites the trigger,
 * and `eventTextMeReminder.test.ts` asserts the SQL still says what the
 * comments claim.
 *
 * The rules, from the trigger:
 *
 *   1. An all-day event fires at 08:00 local. ⚠️ NOT 09:00 — the .ics
 *      builder's timeless placeholder is 09:00 and they are different
 *      questions; Kasandra asked for 8.
 *   2. A missing start_time on a timed event ALSO falls back to 08:00, which
 *      is `coalesce(..., time '08:00')` in the trigger.
 *   3. fire = event start − offset.
 *   4. Land it in 07:00–20:49 by clamping WITHIN THE SAME LOCAL DAY: before
 *      07:00 → 07:00, at/after 20:50 → 20:50.
 *   5. If the clamped instant is in the past, or later than the event itself,
 *      the reminder cannot be placed at all.
 *
 * ⚠️ DO NOT reuse `holdUntilQuietEnd` for step 4. It pushes a 21:00+ instant
 * to 07:00 THE NEXT MORNING, which for a reminder means arriving after the
 * event it was about. It is the right helper for a recurring task's slot and
 * the wrong one here.
 *
 * ⚠️ And step 4's first draft (in the migration's own history) snapped forward
 * to 07:00 on the EVENT's day rather than on the day the fire time landed on.
 * That destroys the offset: "1 week before" on a 06:00 Saturday game computes
 * to 06:00 the previous Saturday, and moving that to 07:00 on the day of the
 * game turns a week of notice into none.
 */

import {
  QUIET_END_HOUR,
  QUIET_START_HOUR,
  localPartsOf,
  zonedWallClockToUtc,
} from "./scheduleNextRun";

/**
 * The clock time an all-day event's reminder is measured from.
 *
 * ⚠️ Pinned to the trigger's `time '08:00'`, not to `DEFAULT_SCHEDULE_HOUR`,
 * even though both are 8 today. They answer different questions and a future
 * change to the morning-digest hour must not silently move event reminders.
 */
export const EVENT_REMINDER_ALL_DAY_HOUR = 8;

/**
 * How far before 21:00 the last deliverable slot sits.
 *
 * run-scheduled-tasks evaluates quiet hours at SEND time and SUPPRESSES rather
 * than defers, and pg_cron calls it every five minutes — so a tick that starts
 * a little late still has to land before 21:00. Ten minutes, matching
 * `TIMED_REMINDER_DELIVERY_MARGIN_MINUTES` in the chat builder and the
 * `21 * 60 - 10` in the trigger. The test asserts all three agree.
 */
export const EVENT_REMINDER_DELIVERY_MARGIN_MINUTES = 10;

/** First deliverable minute of the local day: 07:00. */
export const FIRST_DELIVERABLE_MINUTE = QUIET_END_HOUR * 60;
/** Last deliverable minute of the local day: 20:50. */
export const LAST_DELIVERABLE_MINUTE =
  QUIET_START_HOUR * 60 - EVENT_REMINDER_DELIVERY_MARGIN_MINUTES;

export interface EventTextMeInput {
  userId: string;
  /** ⚠️ `events.id`, which is what the FK and the trigger's `new.id` are.
   *  NOT `imported_events.id` — they happen to be equal for rows written by
   *  writeKinEvents (it spreads one generated id into both tables) and are
   *  NOT equal for a row that arrived any other way. */
  eventId: string;
  title: string;
  /** YYYY-MM-DD on her calendar. */
  eventDate: string;
  /** "HH:MM" or "HH:MM:SS" on her wall clock, or null for TBD/all-day. */
  startTime: string | null;
  isAllDay: boolean;
  /** Minutes before the event. 0 is "at the time of the event" and is real. */
  offsetMinutes: number;
  /** `user_preferences.notifications_timezone` and nothing else — the column
   *  the runner gates both channels on, and the one the trigger reads. */
  timeZone: string | null;
  nowIso: string;
}

export type EventTextMeRow = {
  user_id: string;
  task_type: "timed_reminder";
  event_id: string;
  display_name: string;
  /** null, deliberately: a cadence on a one-off survives the off switch and
   *  re-fires. Same rule as every other one-off row. */
  interval_days: null;
  schedule_time: string;
  next_run_at: string;
  is_active: true;
  config: {
    task_description: string;
    event_id: string;
    offset_minutes: number;
    event_title: string;
    local_date: string;
    local_time: string;
    snapped_out_of_quiet_hours: boolean;
  };
};

export type EventTextMeFailure =
  /** No notifications timezone, so no instant can be computed. */
  | "no_timezone"
  | "bad_date"
  | "bad_time"
  /** The slot has already gone by. */
  | "in_past"
  /** Clamping it into the deliverable window pushed it past the event. */
  | "no_slot";

export type EventTextMeBuild =
  | {
      ok: true;
      row: EventTextMeRow;
      /** True when the fire time had to be moved into 07:00–20:49. The UI has
       *  to say so — she chose an offset and is getting a different one. */
      snapped: boolean;
      /** The local wall clock she will actually be texted at, for that copy. */
      localDate: string;
      localTime: string;
    }
  | { ok: false; reason: EventTextMeFailure };

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Builds the one `scheduled_tasks` row for "text me about this one".
 *
 * Returns a reason rather than throwing: every failure here has to turn into a
 * line she can read, and three of the five are things she can act on.
 */
export function buildEventTextMeRow(input: EventTextMeInput): EventTextMeBuild {
  const { timeZone } = input;
  if (!timeZone) return { ok: false, reason: "no_timezone" };

  if (!DATE_RE.test(input.eventDate)) return { ok: false, reason: "bad_date" };
  const [y, m, d] = input.eventDate.split("-").map((n) => parseInt(n, 10));
  // Date.UTC NORMALISES 2026-02-31 into March 3rd, so a bad date would place a
  // reminder on a day she never named. Round-trip it instead.
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) {
    return { ok: false, reason: "bad_date" };
  }

  // Rules 1 and 2: all-day fires from 08:00, and so does a timed event with no
  // start_time. `coalesce(case when is_all_day then '08:00' else start_time
  // end, '08:00')` in the trigger — note BOTH arms end at 08:00.
  let hours = EVENT_REMINDER_ALL_DAY_HOUR;
  let minutes = 0;
  if (!input.isAllDay && input.startTime) {
    const match = TIME_RE.exec(input.startTime);
    if (!match) return { ok: false, reason: "bad_time" };
    hours = parseInt(match[1], 10);
    minutes = parseInt(match[2], 10);
  }

  const eventInstant = zonedWallClockToUtc(y, m - 1, d, hours, minutes, timeZone);
  if (!eventInstant) return { ok: false, reason: "no_timezone" };

  const now = new Date(input.nowIso);
  if (!Number.isFinite(now.getTime())) return { ok: false, reason: "in_past" };

  // Rule 3.
  const offset = Number.isFinite(input.offsetMinutes) ? Math.max(0, Math.trunc(input.offsetMinutes)) : 0;
  let fireAt = new Date(eventInstant.getTime() - offset * 60_000);

  // Rule 4 — clamp within the SAME local day as the fire time.
  let parts = localPartsOf(fireAt, timeZone);
  if (!parts) return { ok: false, reason: "no_timezone" };
  const fireMinute = parts.hours * 60 + parts.minutes;
  let snapped = false;
  if (fireMinute < FIRST_DELIVERABLE_MINUTE) {
    const moved = zonedWallClockToUtc(parts.year, parts.month, parts.day, QUIET_END_HOUR, 0, timeZone);
    if (!moved) return { ok: false, reason: "no_timezone" };
    fireAt = moved;
    snapped = true;
  } else if (fireMinute >= LAST_DELIVERABLE_MINUTE) {
    const moved = zonedWallClockToUtc(
      parts.year,
      parts.month,
      parts.day,
      Math.floor(LAST_DELIVERABLE_MINUTE / 60),
      LAST_DELIVERABLE_MINUTE % 60,
      timeZone,
    );
    if (!moved) return { ok: false, reason: "no_timezone" };
    fireAt = moved;
    snapped = true;
  }

  // Rule 5. Two distinct answers, because they are two different things to
  // say: the event is over, versus there is no minute left that works.
  if (fireAt.getTime() <= now.getTime()) {
    return { ok: false, reason: snapped ? "no_slot" : "in_past" };
  }
  if (fireAt.getTime() > eventInstant.getTime()) {
    return { ok: false, reason: "no_slot" };
  }

  if (snapped) {
    parts = localPartsOf(fireAt, timeZone);
    if (!parts) return { ok: false, reason: "no_timezone" };
  }

  const localDate = `${parts.year}-${pad(parts.month + 1)}-${pad(parts.day)}`;
  const localTime = `${pad(parts.hours)}:${pad(parts.minutes)}`;
  const title = input.title.trim().slice(0, 200) || "your event";
  const taskDescription = `${title}`;

  return {
    ok: true,
    snapped,
    localDate,
    localTime,
    row: {
      user_id: input.userId,
      task_type: "timed_reminder",
      event_id: input.eventId,
      display_name: title,
      interval_days: null,
      schedule_time: `${localTime}:00`,
      next_run_at: fireAt.toISOString(),
      is_active: true,
      config: {
        task_description: taskDescription,
        // ⚠️ Also in `config`, not only in the column. The trigger reads the
        // column; the Tasks card and any later backfill read config, and
        // Kasandra's brief named config explicitly.
        event_id: input.eventId,
        offset_minutes: offset,
        event_title: title,
        local_date: localDate,
        local_time: localTime,
        snapped_out_of_quiet_hours: snapped,
      },
    },
  };
}
